import { MfsCollection, MfsItem, MfsSubCollectionId, MfsCollectionId, MfsCollectionKind } from './mfsTypes';
import { MfsCollectionProperties, RequiredMfsItemProperties } from './mfsProperty';
import { isString } from '../utility';

/**
 * Returns true, if all required properties of `MfsItem` are present on the item; false, otherwise. Does not check the
 * values of properties but just presence.
 *
 * @param item The item to check
 */
export function isMfsItem(item: Partial<MfsItem>, select?: string[]): item is MfsItem {
  if (select) {
    if (select.includes('label') && !item.label) {
      return false;
    }
    if (select.includes('displayText') && !item.displayText) {
      return false;
    }
    // item ID should always be present
    return !!item.id;
  } else {
    return ensureAllPropertiesExist(item, RequiredMfsItemProperties);
  }
}

/**
 * Returns true, if all required properties of `MfsCollection` are present on the item; false, otherwise. Does not check
 * the values of properties but just presence.
 *
 * @param item The item to check
 */
export function isMfsCollection(item: Partial<MfsItem>): item is MfsCollection {
  return ensureAllPropertiesExist(item, MfsCollectionProperties) && item.kind === MfsCollectionKind.OrderedList;
}

/** Checks if the given id is {@link MfsCollectionId}. */
export function isMfsCollectionId(collectionId: MfsCollectionId | MfsSubCollectionId): collectionId is MfsCollectionId {
  return isString(collectionId);
}

/** All the required keys of type T. */
export type RequiredKeys<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof T]-?: {} extends Pick<T, K> ? never : K;
}[keyof T];

/**
 * Checks if the given `item` has all the required properties of type `T`. Use this method to define app-specific type guards.
 *
 * @example
 *   type MyAppItem = MfsItem & {
 *     s?: string;
 *     b: boolean;
 *   };
 *
 *   // a type-safe way to define all required properties (this excludes optional property `b`)
 *   const requiredProps: Record<RequiredKeys<MyAppItem>, boolean> = {
 *     b: true,
 *   };
 *
 *   // you can define this type guard once, and use in all MfsDataStore methods when you need to read a MyAppItem
 *   const isMyAppItem = (item: MfsItem): item is MyAppItem => hasProperties(item, requiredProps);
 *
 *   // e.g item is of type MyAppItem | undefined
 *   const item = await msf.getItem(id, isMyAppItem);
 *
 * @param item The item to check
 * @param appProperties The required properties to check.
 * @returns True, if the item has all the required properties of type `T`; false, otherwise.
 */
export function hasProperties<T extends MfsItem>(
  item: Partial<MfsItem>,
  appProperties: Record<RequiredKeys<T>, boolean>
): item is T {
  return ensureAllPropertiesExist(item, appProperties);
}

/**
 * Specifies whether all property keys with value `true` from the `properties` record exist on the `input` object.
 *
 * @param input The object to check
 * @param properties Properties to check. If you set propertyKey to false, it will be excluded from the checks.
 */
export function ensureAllPropertiesExist(input: Record<string, unknown>, properties: Record<string, boolean>): boolean {
  for (const k in properties) {
    if (properties[k] && !(k in input)) {
      return false;
    }
  }
  return true;
}
